import {
  PERSONAL_AWARD_META,
  SOCIAL_AWARD_META,
  WORKOUT_TROPHY_TARGET,
  workoutTrophyMeta,
  type PersonalAward,
  type SocialAward,
  type TrophyMonth,
} from '../../domain/awards/types';
import { formatMonthKey } from '../../domain/leaderboard/types';
import { countTrophies } from '../../domain/awards/computeAwards';
import './Awards.css';

/** "Aug 4" - parsed from parts so it does not read a day early west of UTC. */
function shortDate(workoutDate: string): string {
  const [year, month, day] = workoutDate.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

function personalMeta(award: PersonalAward, monthKey: string) {
  return award.type === 'workouts' ? workoutTrophyMeta(monthKey) : PERSONAL_AWARD_META[award.type];
}

/** The sub-line under a trophy: what it took, or how far off it is. */
function personalSub(award: PersonalAward, monthKey: string): string {
  const { have, need } = award.progress ?? { have: 0, need: 0 };

  // The monthly workout trophy reads as what it was for, earned or not - the
  // date it landed says less than the thing you did.
  if (award.type === 'workouts') {
    const month = formatMonthKey(monthKey).split(' ')[0];
    return award.earned
      ? `${WORKOUT_TROPHY_TARGET} workouts in ${month}`
      : `${have} of ${WORKOUT_TROPHY_TARGET} workouts in ${month}`;
  }

  if (award.earned) return award.earnedDate ? shortDate(award.earnedDate) : 'Earned';
  if (award.type === 'finisher') return `${have} of ${need} categories`;
  return `${have} of ${need}`;
}

function SocialTrophy({ award }: { award: SocialAward }) {
  const meta = SOCIAL_AWARD_META[award.type];
  return (
    <li className="trophy trophy--social">
      <span className="trophy__icon" aria-hidden="true">
        {meta.icon}
      </span>
      <span className="trophy__name">{meta.short}</span>
      <span className="trophy__sub">
        {award.value.toLocaleString()} {award.unit === 'points' ? 'pts' : 'lb'}
      </span>
    </li>
  );
}

function PersonalTrophy({ award, monthKey }: { award: PersonalAward; monthKey: string }) {
  const meta = personalMeta(award, monthKey);
  const { have, need } = award.progress ?? { have: 0, need: 1 };
  return (
    <li className={`trophy ${award.earned ? '' : 'trophy--locked'}`}>
      <span className="trophy__icon" aria-hidden="true">
        {meta.icon}
      </span>
      <span className="trophy__name">{meta.label}</span>
      <span className="trophy__sub">{personalSub(award, monthKey)}</span>
      {!award.earned && (
        <span className="trophy__bar">
          <span style={{ width: `${Math.round((have / need) * 100)}%` }} />
        </span>
      )}
    </li>
  );
}

export interface TrophyCaseProps {
  months: TrophyMonth[];
  isLoading?: boolean;
  /**
   * Hides the unearned trophies. Somebody else's case should show what they
   * have won, not how close they are to the next one.
   */
  earnedOnly?: boolean;
}

export function TrophyCase({ months, isLoading = false, earnedOnly = false }: TrophyCaseProps) {
  if (isLoading) return <div className="leaderboard__loading">Loading...</div>;
  if (months.length === 0) {
    return <div className="leaderboard__empty">No trophies yet. Log a lift to start.</div>;
  }

  const total = countTrophies(months);

  return (
    <div className="trophy-case">
      <p className="trophy-case__total">
        <span aria-hidden="true">🏆</span> {total} {total === 1 ? 'trophy' : 'trophies'}
      </p>
      {months.map((month) => {
        const personal = earnedOnly ? month.personal.filter((a) => a.earned) : month.personal;
        if (personal.length === 0 && month.social.length === 0) return null;
        return (
          <section key={month.monthKey}>
            <h4 className="trophy-case__month">
              {formatMonthKey(month.monthKey)}
              {month.isCurrentMonth && <span> · in progress</span>}
            </h4>
            <ul className="trophy-case__grid">
              {month.social.map((award) => (
                <SocialTrophy key={award.type} award={award} />
              ))}
              {personal.map((award) => (
                <PersonalTrophy key={award.type} award={award} monthKey={month.monthKey} />
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
